import React from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, ComposedChart, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { THEME } from '../constants';

const CustomBoxPlot = ({ x, y, width, height, payload }) => {
    if (!payload || !x || !y || !width || !height) return null;

    const datum = payload;
    if (!datum || !datum.min || !datum.max) return null;

    const boxWidth = width * 0.4;
    const centerX = x + width / 2;

    const getYPosition = (value) => {
        if (!value || !datum.max) return y + height;
        return y + height - (height * (value / datum.max));
    };

    return (
        <g>
            {/* Vertical Whiskers */}
            <line
                x1={centerX}
                y1={getYPosition(datum.min)}
                x2={centerX}
                y2={getYPosition(datum.q1)}
                stroke={datum.color || THEME.colors.win}
                strokeWidth={2}
            />
            <line
                x1={centerX}
                y1={getYPosition(datum.q3)}
                x2={centerX}
                y2={getYPosition(datum.max)}
                stroke={datum.color || THEME.colors.win}
                strokeWidth={2}
            />

            {/* Box */}
            <rect
                x={centerX - boxWidth / 2}
                y={getYPosition(datum.q3)}
                width={boxWidth}
                height={getYPosition(datum.q1) - getYPosition(datum.q3)}
                fill={datum.color || THEME.colors.win}
                fillOpacity={0.3}
                stroke={datum.color || THEME.colors.win}
                strokeWidth={2}
            />

            {/* Median line */}
            <line
                x1={centerX - boxWidth / 2}
                y1={getYPosition(datum.median)}
                x2={centerX + boxWidth / 2}
                y2={getYPosition(datum.median)}
                stroke={datum.color || THEME.colors.win}
                strokeWidth={2}
            />

            {/* Horizontal Whisker Caps */}
            <line
                x1={centerX - boxWidth / 4}
                y1={getYPosition(datum.min)}
                x2={centerX + boxWidth / 4}
                y2={getYPosition(datum.min)}
                stroke={datum.color || THEME.colors.win}
                strokeWidth={2}
            />
            <line
                x1={centerX - boxWidth / 4}
                y1={getYPosition(datum.max)}
                x2={centerX + boxWidth / 4}
                y2={getYPosition(datum.max)}
                stroke={datum.color || THEME.colors.win}
                strokeWidth={2}
            />
        </g>
    );
};

CustomBoxPlot.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    payload: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
        q1: PropTypes.number,
        q3: PropTypes.number,
        median: PropTypes.number,
        color: PropTypes.string
    })
};

const BoxPlotTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;

    const data = payload[0].payload;
    return (
        <div className="custom-tooltip">
            <p className="label">{data.name}</p>
            <p>Max: {data.max.toFixed(1)} hrs</p>
            <p>Q3: {data.q3.toFixed(1)} hrs</p>
            <p>Median: {data.median.toFixed(1)} hrs</p>
            <p>Q1: {data.q1.toFixed(1)} hrs</p>
            <p>Min: {data.min.toFixed(1)} hrs</p>
        </div>
    );
};

BoxPlotTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({
        payload: PropTypes.shape({
            name: PropTypes.string,
            min: PropTypes.number,
            q1: PropTypes.number,
            median: PropTypes.number,
            q3: PropTypes.number,
            max: PropTypes.number
        })
    }))
};

const renderCustomBoxPlot = (props) => <CustomBoxPlot {...props} />;

// Add these colors to your THEME or define them here
const LEAD_TIME_COLORS = {
    early: '#8884d8',  // purple/blue
    late: '#82ca9d'    // teal/sage
};

const LeadTimeTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length === 0) return null;

    return (
        <div className="custom-tooltip" style={{ color: '#ffffff' }}>
            <p style={{ color: '#ffffff' }}>{payload[0].payload.username}</p>
            <p style={{ color: '#ffffff' }}>
                {`${payload[0].value.toFixed(2)} hours`}
            </p>
        </div>
    );
};

LeadTimeTooltip.propTypes = {
    active: PropTypes.bool,
    label: PropTypes.string,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            payload: PropTypes.shape({
                username: PropTypes.string.isRequired
            }),
            value: PropTypes.number
        })
    )
};

LeadTimeTooltip.defaultProps = {
    active: false,
    label: '',
    payload: []
};

const LeadTimeAnalysis = ({ data }) => {
    if (!data) return null;

    // Improved sorting function that handles numeric and 'T' prefixed ranks
    const sortedData = [...data].sort((a, b) => {
        // Remove 'T' prefix and convert to number
        const rankA = parseInt(String(a.rank).replace('T', ''));
        const rankB = parseInt(String(b.rank).replace('T', ''));

        // Debug the sorting
        console.log(`Comparing ${a.username}(${rankA}) with ${b.username}(${rankB})`);

        // If ranks are equal, keep original order
        if (rankA === rankB) {
            return data.indexOf(a) - data.indexOf(b);
        }

        return rankA - rankB;
    });

    console.log('Sorted data:', sortedData.map(d => `${d.username}(${d.rank})`));

    // Calculate box plot data
    const calculateBoxPlotData = (arr) => {
        const sorted = [...arr].sort((a, b) => a - b);
        const q1Index = Math.floor(sorted.length * 0.25);
        const q3Index = Math.floor(sorted.length * 0.75);
        const medianIndex = Math.floor(sorted.length * 0.5);

        return {
            min: sorted[0],
            q1: sorted[q1Index],
            median: sorted[medianIndex],
            q3: sorted[q3Index],
            max: sorted[sorted.length - 1]
        };
    };

    // Aggregate data for win/loss box plots
    const winLossData = data.reduce((acc, user) => {
        if (user.avg_winning_lead_time) acc.wins.push(user.avg_winning_lead_time);
        if (user.avg_losing_lead_time) acc.losses.push(user.avg_losing_lead_time);
        return acc;
    }, { wins: [], losses: [] });

    const boxPlotData = [
        {
            name: "Winning Picks",
            ...calculateBoxPlotData(winLossData.wins),
            color: THEME.colors.win
        },
        {
            name: "Losing Picks",
            ...calculateBoxPlotData(winLossData.losses),
            color: THEME.colors.loss
        }
    ];

    return (
        <div className="lead-time-analysis">
            <div className="chart-container lead-time-box-plot">
                <h3>W/L Vs. Lead Time</h3>
                <ResponsiveContainer width="100%" height={450}>
                    <ComposedChart
                        data={boxPlotData}
                        layout="horizontal"
                        margin={{ top: 20, right: 30, left: 60, bottom: 20 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={THEME.colors.grid}
                        />
                        <YAxis
                            type="number"
                            domain={[0, 'auto']}
                            tick={{ fill: THEME.colors.text }}
                            label={{
                                value: 'Hours Before Game',
                                angle: -90,
                                position: 'insideLeft',
                                fill: THEME.colors.text,
                                style: { textAnchor: 'middle' }
                            }}
                        />
                        <XAxis
                            dataKey="name"
                            type="category"
                            height={50}
                            tick={{ fill: THEME.colors.text }}
                        />
                        <Tooltip content={BoxPlotTooltip} />
                        <Bar
                            dataKey="max"
                            shape={renderCustomBoxPlot}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            <div className="chart-container user-lead-times">
                <h3>Average Lead Time by User</h3>
                <div style={{ display: 'flex', gap: '10px', height: '100%' }}>
                    <div className="distribution-position" style={{
                        padding: '10px',
                        alignSelf: 'center',
                        marginTop: '0px',  // Reduced from 50px
                        minWidth: '140px'   // Ensure consistent width
                    }}>
                        <div className="distribution-row">
                            <h4>Tends to win with:</h4>
                            <div className="distribution-values" style={{ gridTemplateColumns: '1fr' }}>
                                <div className="value-card" style={{ borderLeft: `3px solid ${LEAD_TIME_COLORS.early}` }}>
                                    <span className="label">Earlier Locks</span>
                                </div>
                                <div className="value-card" style={{ borderLeft: `3px solid ${LEAD_TIME_COLORS.late}` }}>
                                    <span className="label">Later Locks</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={425}>
                        <BarChart
                            data={sortedData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                            barSize={20}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={THEME.colors.grid}
                            />
                            <XAxis
                                type="number"
                                tick={{ fill: THEME.colors.text, fontSize: THEME.fontSize.axis }}
                            />
                            <YAxis
                                dataKey="username"
                                type="category"
                                width={90}
                                tick={{
                                    fill: THEME.colors.text,
                                    fontSize: '11px'
                                }}
                                interval={0}
                            />
                            <Tooltip
                                content={<LeadTimeTooltip />}
                                cursor={{
                                    fill: 'rgba(255, 255, 255, 0.1)',
                                    radius: 2
                                }}
                            />
                            <Bar
                                dataKey="avg_lead_time"
                                name="Average Lead Time"
                            >
                                {data.map((entry) => (
                                    <Cell
                                        key={`cell-${entry.username}`}
                                        fill={entry.avg_winning_lead_time > entry.avg_losing_lead_time ?
                                            LEAD_TIME_COLORS.early : LEAD_TIME_COLORS.late}
                                        fillOpacity={0.9}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

LeadTimeAnalysis.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        username: PropTypes.string.isRequired,
        avg_winning_lead_time: PropTypes.number,
        avg_losing_lead_time: PropTypes.number,
        avg_lead_time: PropTypes.number
    }))
};

// Add the CSS for the legend
const styles = `
.chart-with-legend {
    display: flex;
    align-items: flex-start;
}

.lead-time-legend {
    padding: 10px;
    margin-right: 20px;
}

.legend-title {
    margin-bottom: 8px;
    color: ${THEME.colors.text};
}

.legend-item {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    color: ${THEME.colors.text};
}

.color-box {
    width: 12px;
    height: 12px;
    margin-right: 8px;
    border-radius: 2px;
}
`;

// Add this style tag to your component or your CSS file
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default LeadTimeAnalysis;
